import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchExchangeProducts } from "../api/backend.js";
import Page from "../components/layout/Page.jsx";
import SidePanel from "../components/SidePanel.jsx";
import ProductSearch from "../components/ProductSearch.jsx";
import VariantPicker from "../components/VariantPicker.jsx";
import { Alert, Button, Card, EmptyState } from "../components/ui.jsx";
import { useOccurrence } from "../context/OccurrenceContext.jsx";
import { requestParentScroll, useIframeHeight } from "../hooks/useIframeHeight.js";
import {
  EXCHANGE_POLICY,
  normalizeExchangePolicy,
  policyCopy,
  variantId,
  variantPrice,
} from "../utils/exchangePolicy.js";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_PAGE_SIZE,
  annotateVariants,
  catalogProductSummary,
  emptyPageInfo,
  normalizeProductPage,
  sameProductVariants,
  shouldSearch,
} from "../utils/productSearch.js";
import { formatBRL } from "../utils/productSearch.js";
import styles from "./Exchange.module.css";

const Exchange = () => {
  const { shop, name, email } = useParams();
  const { occurrence, setOccurrence, resolutionType, resolutionSettings } = useOccurrence();
  const [choices, setChoices] = useState({});
  const [mode, setMode] = useState({});
  const [queries, setQueries] = useState({});
  const [catalogs, setCatalogs] = useState({});
  const [pageInfo, setPageInfo] = useState({});
  const [searching, setSearching] = useState({});
  const [loadingMore, setLoadingMore] = useState({});
  const [picked, setPicked] = useState({});
  const [optionSel, setOptionSel] = useState({});
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const products = useMemo(() => occurrence?.products || [], [occurrence]);
  const policy = normalizeExchangePolicy(resolutionSettings?.exchange?.policy);
  const allowsCatalog = policy !== EXCHANGE_POLICY.sameProduct;

  useIframeHeight();

  useEffect(() => {
    if (!occurrence?.products?.length || (resolutionType && resolutionType !== "exchange")) {
      navigate(email ? `/${shop}/resolution/${name}/${email}` : `/${shop}`);
    }
  }, [email, name, navigate, occurrence, resolutionType, shop]);

  useEffect(() => {
    if (!allowsCatalog) {
      setCatalogs({});
      setPageInfo({});
      return undefined;
    }
    const timers = products.map((product) => {
      const query = queries[product.sku_id] || "";
      return setTimeout(async () => {
        if (!shouldSearch(query)) {
          setCatalogs((prev) => ({ ...prev, [product.sku_id]: [] }));
          setPageInfo((prev) => ({ ...prev, [product.sku_id]: emptyPageInfo() }));
          setSearching((prev) => ({ ...prev, [product.sku_id]: false }));
          return;
        }
        setSearching((prev) => ({ ...prev, [product.sku_id]: true }));
        try {
          const maxPrice = policy === EXCHANGE_POLICY.upToSamePrice
            ? Number(product.price) || 0
            : undefined;
          const page = normalizeProductPage(await fetchExchangeProducts(shop, {
            name,
            email,
            query,
            maxPrice,
            first: SEARCH_PAGE_SIZE,
          }));
          setCatalogs((prev) => ({ ...prev, [product.sku_id]: page.products }));
          setPageInfo((prev) => ({ ...prev, [product.sku_id]: page.pageInfo }));
        } catch {
          setCatalogs((prev) => ({ ...prev, [product.sku_id]: [] }));
          setPageInfo((prev) => ({ ...prev, [product.sku_id]: emptyPageInfo() }));
        } finally {
          setSearching((prev) => ({ ...prev, [product.sku_id]: false }));
        }
      }, SEARCH_DEBOUNCE_MS);
    });
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [allowsCatalog, email, name, policy, products, queries, shop]);

  const localBySku = useMemo(() => {
    const next = {};
    products.forEach((product) => {
      next[product.sku_id] = annotateVariants(product, sameProductVariants(product), policy);
    });
    return next;
  }, [policy, products]);

  const currentMode = (product) => {
    const chosen = mode[product.sku_id];
    if (chosen === "same" || chosen === "other") {
      return chosen;
    }
    const hasLocal = (localBySku[product.sku_id] || []).some((option) => option.allowed);
    return hasLocal || !allowsCatalog ? "same" : "other";
  };

  const selectedProductFor = (product) => {
    if (currentMode(product) === "other") {
      return picked[product.sku_id] || null;
    }
    return {
      id: product.product_id,
      product_id: product.product_id,
      title: product.title || product.name,
      image: product.image,
      options: product.options || [],
      variants: sameProductVariants(product),
    };
  };

  const optionsFor = (product) => {
    const selected = selectedProductFor(product);
    if (currentMode(product) === "other") {
      return annotateVariants(product, selected?.variants || [], policy);
    }
    return localBySku[product.sku_id] || [];
  };

  const dueTotal = products.reduce((sum, product) => {
    const selected = optionsFor(product).find((option) => String(variantId(option.variant)) === String(choices[product.sku_id]));
    return sum + Number(selected?.dueAmount || 0);
  }, 0);

  const handleLoadMore = async (product) => {
    const skuId = product.sku_id;
    const cursor = pageInfo[skuId]?.endCursor;
    if (!cursor || loadingMore[skuId]) {
      return;
    }
    setLoadingMore((prev) => ({ ...prev, [skuId]: true }));
    try {
      const maxPrice = policy === EXCHANGE_POLICY.upToSamePrice
        ? Number(product.price) || 0
        : undefined;
      const page = normalizeProductPage(await fetchExchangeProducts(shop, {
        name,
        email,
        query: queries[skuId] || "",
        maxPrice,
        after: cursor,
        first: SEARCH_PAGE_SIZE,
      }));
      setCatalogs((prev) => ({
        ...prev,
        [skuId]: [...(prev[skuId] || []), ...page.products],
      }));
      setPageInfo((prev) => ({ ...prev, [skuId]: page.pageInfo }));
    } catch {
      setPageInfo((prev) => ({
        ...prev,
        [skuId]: { ...(prev[skuId] || emptyPageInfo()), hasNextPage: false },
      }));
    } finally {
      setLoadingMore((prev) => ({ ...prev, [skuId]: false }));
    }
  };

  const handleMode = (skuId, nextMode) => {
    setMode((prev) => ({ ...prev, [skuId]: nextMode }));
    setChoices((prev) => ({ ...prev, [skuId]: "" }));
    setOptionSel((prev) => ({ ...prev, [skuId]: {} }));
    if (nextMode === "same") {
      setPicked((prev) => ({ ...prev, [skuId]: null }));
    }
    setMessage("");
  };

  const handleContinue = () => {
    const invalid = products.some((product) => {
      const options = optionsFor(product).filter((option) => option.allowed);
      const chosen = options.find((option) => String(variantId(option.variant)) === String(choices[product.sku_id]));
      return options.length > 0 && !chosen;
    });
    if (invalid) {
      setMessage("Escolha uma opção permitida pela política da loja, com estoque.");
      return;
    }
    setOccurrence({
      ...occurrence,
      products: products.map((product) => {
        const chosen = optionsFor(product).find((option) => String(variantId(option.variant)) === String(choices[product.sku_id]));
        return {
          ...product,
          action: product.action === "Garantia" ? "Garantia" : "Troca",
          actionWarranty: product.action === "Garantia" ? "Troca" : product.actionWarranty,
          credit: false,
          newOrderVariantId: choices[product.sku_id] || product.variant_id,
          newOrderProductId: chosen?.variant?.product_id || product.product_id,
          newVariantTitle: chosen?.variant?.title || chosen?.title || "",
          newOrderPrice: chosen ? variantPrice(chosen.variant, product.price) : product.price,
          dueAmount: chosen?.dueAmount || 0,
        };
      }),
    });
    requestParentScroll();
    navigate(`/${shop}/post/${name}${email ? `/${encodeURIComponent(email)}` : ""}`);
  };

  const aside = (
    <SidePanel
      title="Sua troca"
      items={products.map((product) => {
        const chosen = optionsFor(product).find((option) => String(variantId(option.variant)) === String(choices[product.sku_id]));
        return {
          key: product.sku_id,
          image: product.image,
          title: product.title || product.name,
          meta: chosen ? `Trocar por: ${chosen.variant?.title || "opção escolhida"}` : "Escolha a nova opção",
          price: product.price,
        };
      })}
      totalLabel={dueTotal > 0 ? "Diferença a pagar" : undefined}
      total={dueTotal > 0 ? dueTotal : undefined}
      note={dueTotal > 0 ? "A diferença aparece de novo na revisão, antes de confirmar." : "Troca sem custo dentro da política da loja."}
    >
      {message ? <Alert>{message}</Alert> : null}
      <Button onClick={handleContinue}>Continuar</Button>
    </SidePanel>
  );

  return (
    <Page
      aside={aside}
      title="Escolha a troca"
      subtitle={policyCopy(policy)}
      step="exchange"
      onBack={() => navigate(-1)}
    >
      <div className={styles.stack}>
        {products.map((product) => {
          const itemMode = currentMode(product);
          const localOptions = localBySku[product.sku_id] || [];
          const localAllowed = localOptions.filter((option) => option.allowed);
          const selectedProduct = selectedProductFor(product);
          const pickerOptions = optionsFor(product);
          const pickerAllowed = pickerOptions.filter((option) => option.allowed);
          const catalog = catalogs[product.sku_id] || [];
          const resultEntries = catalog.map((entry) => ({
            product: entry,
            summary: catalogProductSummary(entry, product, policy),
          }));
          return (
            <Card key={product.sku_id} className={styles.card}>
              <div className={styles.current}>
                {product.image ? <img src={product.image} alt="" /> : <div className={styles.fallback} aria-hidden="true" />}
                <div>
                  <p className={styles.title}>{product.title || product.name}</p>
                  {product.variantTitle ? <p className={styles.meta}>{product.variantTitle}</p> : null}
                  <p className={styles.meta}>R$ {formatBRL(product.price)}</p>
                </div>
              </div>

              {allowsCatalog ? (
                <div className={styles.modes} role="radiogroup" aria-label={`Como trocar ${product.title || product.name}`}>
                  <button
                    type="button"
                    className={`${styles.mode} ${itemMode === "same" ? styles.modeChecked : ""}`}
                    aria-pressed={itemMode === "same"}
                    onClick={() => handleMode(product.sku_id, "same")}
                  >
                    Outra variação deste produto
                  </button>
                  <button
                    type="button"
                    className={`${styles.mode} ${itemMode === "other" ? styles.modeChecked : ""}`}
                    aria-pressed={itemMode === "other"}
                    onClick={() => handleMode(product.sku_id, "other")}
                  >
                    Outro produto
                  </button>
                </div>
              ) : null}

              {itemMode === "same" ? (
                localAllowed.length === 0 ? (
                  <EmptyState
                    title="Nenhuma variação disponível"
                    description={allowsCatalog
                      ? "Não há tamanho ou cor em estoque. Busque outro produto autorizado pela loja."
                      : "Não há variação permitida com estoque. Volte e escolha outra resolução."}
                  />
                ) : (
                  <VariantPicker
                    product={selectedProduct}
                    item={product}
                    options={localOptions}
                    policy={policy}
                    selectedOptions={optionSel[product.sku_id] || {}}
                    onOptionsChange={(next) => setOptionSel((prev) => ({ ...prev, [product.sku_id]: next }))}
                    selectedVariantId={choices[product.sku_id]}
                    onSelect={(id) => setChoices((prev) => ({ ...prev, [product.sku_id]: id }))}
                  />
                )
              ) : (
                <>
                  <ProductSearch
                    id={`exchange-search-${product.sku_id}`}
                    query={queries[product.sku_id] || ""}
                    onQueryChange={(value) => setQueries((prev) => ({ ...prev, [product.sku_id]: value }))}
                    results={resultEntries}
                    searching={Boolean(searching[product.sku_id])}
                    selectedId={picked[product.sku_id]?.product_id || picked[product.sku_id]?.id}
                    hasNextPage={Boolean(pageInfo[product.sku_id]?.hasNextPage)}
                    loadingMore={Boolean(loadingMore[product.sku_id])}
                    onLoadMore={() => handleLoadMore(product)}
                    onSelect={(next) => {
                      setPicked((prev) => ({ ...prev, [product.sku_id]: next }));
                      setChoices((prev) => ({ ...prev, [product.sku_id]: "" }));
                      setOptionSel((prev) => ({ ...prev, [product.sku_id]: {} }));
                    }}
                  />
                  {selectedProduct ? (
                    pickerAllowed.length === 0 ? (
                      <EmptyState
                        title="Sem variação disponível neste produto"
                        description="Todas as opções estão sem estoque ou fora da política da loja."
                      />
                    ) : (
                      <VariantPicker
                        product={selectedProduct}
                        item={product}
                        options={pickerOptions}
                        policy={policy}
                        selectedOptions={optionSel[product.sku_id] || {}}
                        onOptionsChange={(next) => setOptionSel((prev) => ({ ...prev, [product.sku_id]: next }))}
                        selectedVariantId={choices[product.sku_id]}
                        onSelect={(id) => setChoices((prev) => ({ ...prev, [product.sku_id]: id }))}
                        onChangeProduct={() => {
                          setPicked((prev) => ({ ...prev, [product.sku_id]: null }));
                          setChoices((prev) => ({ ...prev, [product.sku_id]: "" }));
                          setOptionSel((prev) => ({ ...prev, [product.sku_id]: {} }));
                        }}
                      />
                    )
                  ) : null}
                </>
              )}
            </Card>
          );
        })}
      </div>
    </Page>
  );
};

export default Exchange;
